<template>
  <section class="section is-medium blog-section">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.resources-title') }}
        </h2>
      </div>

      <div
        v-if="loading"
        class="has-text-centered"
      >
        <div class="loading-spinner" />
      </div>

      <div
        v-else
        class="columns is-multiline"
      >
        <div
          v-for="(post, index) in posts"
          :key="post._id"
          class="column is-4"
        >
          <div
            class="blog-card animate fade-in-up"
            :class="`animate--delay-${index}`"
          >
            <div
              class="blog-image"
              :style="`background-color: ${getCardColor(index)}`"
            >
              <i :class="getCardIcon(index)" />
            </div>
            <div class="blog-content">
              <h3 class="blog-title">
                {{ currentLocale === 'es' ? post.title_es : post.title }}
              </h3>
              <p class="blog-excerpt">
                {{ currentLocale === 'es' ? post.description_es : post.description }}
              </p>
              <div class="blog-meta">
                <div class="author-info">
                  <img
                    :src="post.author.profile_image"
                    :alt="post.author.name"
                    class="author-image"
                    width="40"
                    height="40"
                    loading="lazy"
                    decoding="async"
                  >
                  <div class="author-details">
                    <span class="author-name">{{ post.author.name }} {{ post.author.last_name }}</span>
                    <span class="publish-date">{{ formatDate(post.publishing_date) }}</span>
                  </div>
                </div>
                <router-link
                  :to="{ name: 'blog-post', params: { id: post.key } }"
                  class="blog-link"
                >
                  <span>{{ $t('general.read-more') }}</span>
                  <i class="fas fa-arrow-right" />
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="has-text-centered mt-5">
        <router-link to="/blog">
          <button class="button is-primary is-medium blog-view-all-btn">
            {{ $t('general.view-all') }}
          </button>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import blogService from '@/services/blogService';

export default {
  name: 'BlogSection',
  data() {
    return {
      posts: [],
      loading: true,
      cardColors: ['#4A7FEC', '#25BDB0', '#FB6CBC'], // indiefy-blue, indiefy-teal, indiefy-pink
      cardIcons: ['fas fa-headphones', 'fas fa-image', 'fas fa-bullhorn'],
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
  },
  async created() {
    try {
      const response = await blogService.getAll({
        limit: 3,
        sort: '-publishing_date',
        published: 1,
      });
      this.posts = response.items;
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    getCardColor(index) {
      return this.cardColors[index % this.cardColors.length];
    },
    getCardIcon(index) {
      return this.cardIcons[index % this.cardIcons.length];
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString(this.currentLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/animations";

.blog-section {
  background-color: var(--color-background-alt, #f8f9fa);
}

.title.has-line {
  position: relative;
  padding-bottom: 1.5rem;
  color: var(--color-text, #000);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    border-radius: 2px;
  }
}

.blog-card {
  height: 100%;
  background-color: var(--color-background, white);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);

    .blog-link {
      color: darken($indiefy-blue, 10%);

      i {
        transform: translateX(5px);
      }
    }
  }
}

.blog-image {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 3rem;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
}

.blog-content {
  padding: 1.5rem;
}

.blog-title {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--color-text, #333);
  line-height: 1.4;
}

.blog-excerpt {
  font-size: 0.95rem;
  color: var(--color-text-light, #666);
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.blog-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-image {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 1rem;
}

.author-details {
  display: flex;
  flex-direction: column;
}

.author-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text, #333);
}

.publish-date {
  font-size: 0.8rem;
  color: var(--color-text-light, #666);
}

.blog-link {
  display: flex;
  align-items: center;
  color: $indiefy-blue;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: color 0.3s ease;

  i {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
}

.loading-spinner {
  display: inline-block;
  width: 50px;
  height: 50px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: $indiefy-blue;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media screen and (max-width: 768px) {
  .blog-card {
    margin-bottom: 2rem;
  }
}

.blog-view-all-btn {
  min-width: 180px;
  font-size: 1.15rem;
  font-weight: 700;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(74,127,236,0.08);
  letter-spacing: 0.01em;
  transition: background 0.18s, box-shadow 0.18s;
  margin-top: 1.5rem;
  &:hover, &:focus {
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    color: #fff;
    box-shadow: 0 4px 24px rgba(74,127,236,0.13);
  }
}
</style>